/**
 * AdminFlagsPage.jsx
 * Safety flags panel — view and action player block/report flags.
 * Admins can send emails, send Stable Talk messages, or resolve flags.
 */

import { useState, useEffect } from 'react';
import { db }                 from '../../services/firebase';
import {
  collection, query, where, orderBy, onSnapshot,
  updateDoc, doc, addDoc, serverTimestamp,
} from 'firebase/firestore';
import { getFunctions, httpsCallable } from 'firebase/functions';
import './AdminFlagsPage.css';

const fns = (name) => httpsCallable(getFunctions(undefined, 'australia-southeast1'), name);

const FLAG_LABEL = {
  block:  '🚫 Block Report',
  report: '⚠️ Content Report',
  spam:   '📢 Spam Report',
};

export default function AdminFlagsPage() {
  const [flags,      setFlags]      = useState([]);
  const [activeFlag, setActiveFlag] = useState(null);
  const [emailNote,  setEmailNote]  = useState('');
  const [stMsg,      setStMsg]      = useState('');
  const [sending,    setSending]    = useState(false);
  const [status,     setStatus]     = useState(null);

  useEffect(() => {
    const q = query(
      collection(db, 'admin_flags'),
      where('status', '==', 'pending'),
      orderBy('createdAt', 'desc')
    );
    return onSnapshot(q, snap =>
      setFlags(snap.docs.map(d => ({ id: d.id, ...d.data() })))
    );
  }, []);

  function openFlag(flag) {
    setActiveFlag(prev => (prev?.id === flag.id ? null : flag));
    setEmailNote('');
    setStMsg('');
    setStatus(null);
  }

  async function sendEmail(flag) {
    if (!emailNote.trim()) return;
    setSending(true);
    try {
      await fns('adminSendFlagEmail')({
        flagId:    flag.id,
        targetUid: flag.flaggedUid,
        note:      emailNote.trim(),
        context:   flag.context,
      });
      setStatus({ type: 'success', msg: 'Email sent successfully' });
      setEmailNote('');
    } catch (e) {
      setStatus({ type: 'error', msg: `Failed: ${e.message}` });
    } finally {
      setSending(false);
    }
  }

  async function sendStableTalkMessage(flag) {
    if (!stMsg.trim()) return;
    setSending(true);
    try {
      await addDoc(collection(db, 'admin_messages'), {
        toUid:     flag.flaggedUid,
        fromAdmin: true,
        message:   stMsg.trim(),
        flagId:    flag.id,
        createdAt: serverTimestamp(),
        read:      false,
      });
      setStatus({ type: 'success', msg: 'Message queued for delivery' });
      setStMsg('');
    } catch (e) {
      setStatus({ type: 'error', msg: `Failed: ${e.message}` });
    } finally {
      setSending(false);
    }
  }

  async function resolveFlag(flagId, resolution) {
    await updateDoc(doc(db, 'admin_flags', flagId), {
      status:     resolution,
      resolvedAt: serverTimestamp(),
    });
    setActiveFlag(null);
    setStatus(null);
  }

  function formatTime(ts) {
    if (!ts?.toDate) return '';
    return ts.toDate().toLocaleString('en-AU', {
      day: 'numeric', month: 'short',
      hour: 'numeric', minute: '2-digit', hour12: true,
    });
  }

  return (
    <div className="admin-flags">
      <h1 className="admin-flags__title">
        🚩 Safety Flags
        {flags.length > 0 && (
          <span className="admin-flags__count">{flags.length}</span>
        )}
      </h1>
      <p className="admin-flags__desc">
        Player-generated safety reports from Stable Talk. Action each flag below.
      </p>

      {flags.length === 0 ? (
        <div className="admin-flags__empty">✓ No pending flags</div>
      ) : (
        <div className="admin-flags__list">
          {flags.map(flag => (
            <div
              key={flag.id}
              className={`admin-flag-card${activeFlag?.id === flag.id ? ' admin-flag-card--active' : ''}`}
              onClick={() => openFlag(flag)}
            >
              <div className="admin-flag-card__header">
                <span className="admin-flag-card__type">
                  {FLAG_LABEL[flag.type] || '⚠️ Flag'}
                </span>
                <span className="admin-flag-card__time">{formatTime(flag.createdAt)}</span>
              </div>

              <div className="admin-flag-card__body">
                <span className="admin-flag-card__reporter">@{flag.reporterUsername}</span>
                <span className="admin-flag-card__arrow"> blocked </span>
                <span className="admin-flag-card__flagged">@{flag.flaggedUsername}</span>
              </div>

              <p className="admin-flag-card__context">{flag.context}</p>

              {activeFlag?.id === flag.id && (
                <div
                  className="admin-flag-actions"
                  onClick={e => e.stopPropagation()}
                >
                  {status && (
                    <div className={`admin-status admin-status--${status.type}`}>
                      {status.msg}
                    </div>
                  )}

                  {/* Email to flagged user */}
                  <div className="admin-flag-actions__section">
                    <p className="admin-flag-actions__label">
                      📧 Email @{flag.flaggedUsername}
                    </p>
                    <textarea
                      className="admin-form__textarea"
                      placeholder="Message to include in the email to the flagged user…"
                      value={emailNote}
                      onChange={e => setEmailNote(e.target.value)}
                      rows={3}
                    />
                    <button
                      className="admin-btn admin-btn--primary"
                      onClick={() => sendEmail(flag)}
                      disabled={!emailNote.trim() || sending}
                    >
                      Send Email
                    </button>
                  </div>

                  {/* Stable Talk message */}
                  <div className="admin-flag-actions__section">
                    <p className="admin-flag-actions__label">
                      🐎 Stable Talk message to @{flag.flaggedUsername}
                    </p>
                    <textarea
                      className="admin-form__textarea"
                      placeholder="Private message from admin via Stable Talk…"
                      value={stMsg}
                      onChange={e => setStMsg(e.target.value)}
                      rows={3}
                    />
                    <button
                      className="admin-btn admin-btn--primary"
                      onClick={() => sendStableTalkMessage(flag)}
                      disabled={!stMsg.trim() || sending}
                    >
                      Send Stable Talk Message
                    </button>
                  </div>

                  {/* Quick actions */}
                  <div className="admin-flag-actions__section">
                    <p className="admin-flag-actions__label">QUICK ACTIONS</p>
                    <div className="admin-flag-actions__buttons">
                      <button
                        className="admin-btn admin-btn--warning"
                        onClick={() => {
                          window.location.href = `/admin/users?uid=${flag.flaggedUid}`;
                        }}
                      >
                        View @{flag.flaggedUsername}
                      </button>
                      <button
                        className="admin-btn admin-btn--secondary"
                        onClick={() => resolveFlag(flag.id, 'dismissed')}
                      >
                        Dismiss
                      </button>
                      <button
                        className="admin-btn admin-btn--danger"
                        onClick={() => resolveFlag(flag.id, 'escalated')}
                      >
                        Escalate
                      </button>
                    </div>
                  </div>
                </div>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
